import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Users, Plus, Upload, FileText, Send, DollarSign, 
  TrendingUp, Clock, CheckCircle, XCircle, Eye, X,
  Mail, Phone, MessageSquare, Calendar, ChevronRight, Edit2,
  LogOut, Menu, Package, Home, ChevronDown, Building2, MapPin,
  User, Trash2, Download, AlertCircle, Loader2, Instagram, Facebook, Linkedin, Twitter, Globe, Save
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useSettings } from '../contexts/SettingsContext';
import StarRating from '../components/StarRating';
import ContractSection from '../components/ContractSection';

// DataLab Logo Component - Uses settings or default
const DataLabLogo = ({ settings }) => {
  if (settings?.logoUrl) {
    return (
      <img 
        src={settings.logoUrl} 
        alt={settings?.appName || 'Logo'} 
        className="h-10 w-auto object-contain"
      />
    );
  }
  
  // Default DataLab logo
  return (
    <div className="flex items-center gap-2">
      <svg width={36} height={36} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M8 8H20C26.627 8 32 13.373 32 20C32 26.627 26.627 32 20 32H8V8Z" stroke="#9CA3AF" strokeWidth="2.5" fill="none"/>
        <circle cx="20" cy="12" r="2" fill="#9CA3AF"/>
        <circle cx="26" cy="16" r="2" fill="#9CA3AF"/>
        <circle cx="28" cy="22" r="2" fill="#9CA3AF"/>
        <circle cx="24" cy="28" r="2" fill="#9CA3AF"/>
        <circle cx="16" cy="30" r="2" fill="#9CA3AF"/>
        <line x1="8" y1="20" x2="20" y2="20" stroke="#9CA3AF" strokeWidth="2"/>
      </svg>
      <span className="font-bold text-xl">
        <span className="text-gray-400">DATA</span>
        <span className="text-green-500">LAB</span>
      </span>
    </div>
  );
};

// Funnel Stage Component for visual representation
const FunnelStage = ({ stage, count, color, isLast }) => (
  <div className="flex items-center">
    <div 
      className="relative px-4 py-2 text-white text-xs font-medium text-center min-w-[100px]"
      style={{ 
        backgroundColor: color,
        clipPath: isLast ? 'polygon(10% 0%, 100% 0%, 100% 100%, 10% 100%, 0% 50%)' : 'polygon(0% 0%, 90% 0%, 100% 50%, 90% 100%, 0% 100%, 10% 50%)'
      }}
    >
      <div className="font-bold text-lg">{count}</div>
      <div className="truncate max-w-[80px]">{stage}</div>
    </div>
  </div>
);

export default function ReferidorPortal({ currentUser, onLogout }) {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('leads');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedLeadDetail, setSelectedLeadDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // New states for commissions
  const [commissions, setCommissions] = useState([]);
  const [loadingCommissions, setLoadingCommissions] = useState(false);
  const [commissionStats, setCommissionStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    paid: 0,
    rejected: 0
  });
  
  // Theme colors from settings - default to green
  const primaryColor = settings?.primaryColor || '#00b30c';
  const secondaryColor = settings?.secondaryColor || '#64f288';
  const accentColor = settings?.accentColor || '#71f75f';
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+57',
    phone: '',
    description: '',
    documents: []
  });

  // Funnel data
  const [funnelData, setFunnelData] = useState([]);

  const loadMyLeads = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.getMyReferidoLeads();
      setLeads(response.leads || []);
      setStats(response.stats || {});
      
      // Calculate funnel data
      const stageGroups = {};
      (response.leads || []).forEach(lead => {
        const stageName = lead.stageName || 'Sin etapa';
        const stageColor = lead.stageColor || '#6366f1';
        if (!stageGroups[stageName]) {
          stageGroups[stageName] = { name: stageName, count: 0, color: stageColor };
        }
        stageGroups[stageName].count++;
      });
      setFunnelData(Object.values(stageGroups));
    } catch (error) {
      toast.error('Error al cargar leads');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMyLeads();
  }, [loadMyLeads]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const maxSize = 10 * 1024 * 1024;
    
    files.forEach(file => {
      if (file.size > maxSize) {
        toast.error(`${file.name} es demasiado grande. Máximo 10MB`);
        return;
      }
      
      const reader = new FileReader();
      reader.onload = () => {
        setFormData(prev => ({
          ...prev,
          documents: [...prev.documents, {
            name: file.name,
            title: '', // Campo para el título/descripción del documento
            fileData: reader.result.split(',')[1],
            fileType: file.type,
            size: file.size
          }]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const updateDocumentTitle = (index, title) => {
    setFormData(prev => ({
      ...prev,
      documents: prev.documents.map((doc, i) => 
        i === index ? { ...doc, title } : doc
      )
    }));
  };

  const removeDocument = (index) => {
    setFormData(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim()) {
      toast.error('Nombre, correo y teléfono son requeridos');
      return;
    }
    setSubmitting(true);
    try {
      // Combine country code with phone
      const submitData = {
        ...formData,
        phone: `${formData.countryCode} ${formData.phone}`
      };
      await api.createReferidoLead(submitData);
      toast.success('¡Lead referido enviado exitosamente!');
      setShowNewModal(false);
      setFormData({ name: '', email: '', countryCode: '+57', phone: '', description: '', documents: [] });
      loadMyLeads();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Error al enviar lead');
    } finally {
      setSubmitting(false);
    }
  };

  // Close modal handler
  const handleCloseModal = () => {
    setShowNewModal(false);
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { label: 'Pendiente', bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock },
      approved: { label: 'Aprobada', bg: 'bg-blue-100', text: 'text-blue-700', icon: CheckCircle },
      paid: { label: 'Pagada', bg: 'bg-green-100', text: 'text-green-700', icon: DollarSign }
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${c.bg} ${c.text}`}>
        <Icon className="w-3 h-3" />
        {c.label}
      </span>
    );
  };

  const formatCurrency = (value) => {
    if (!value) return '$0';
    return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(value);
  };

  const handleLogout = () => {
    if (onLogout) onLogout();
    navigate('/login');
  };

  const handleViewLeadDetail = async (lead) => {
    setLoadingDetail(true);
    try {
      // Use the lead data from the list which already has stageName and stageColor
      // Then enrich with full details from API
      const fullLead = await api.getLead(lead.id);
      
      // Merge the stage info from the list with the full lead data
      setSelectedLeadDetail({
        ...fullLead,
        stageName: lead.stageName || fullLead.stageName || 'Sin etapa',
        stageColor: lead.stageColor || fullLead.stageColor || '#6366f1'
      });
    } catch (error) {
      console.error('Error loading lead detail:', error);
      // If API fails, still show the basic lead info from the list
      setSelectedLeadDetail({
        ...lead,
        stageName: lead.stageName || 'Sin etapa',
        stageColor: lead.stageColor || '#6366f1'
      });
    } finally {
      setLoadingDetail(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <DataLabLogo settings={settings} />
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              <button 
                onClick={() => setActiveTab('leads')}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'leads' ? 'text-white' : 'text-slate-600 hover:bg-slate-100'
                }`}
                style={activeTab === 'leads' ? { backgroundColor: primaryColor } : {}}
              >
                <Users className="w-4 h-4" />
                Mis Leads
              </button>
              <button 
                onClick={() => setActiveTab('commissions')}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'commissions' ? 'text-white' : 'text-slate-600 hover:bg-slate-100'
                }`}
                style={activeTab === 'commissions' ? { backgroundColor: primaryColor } : {}}
              >
                <DollarSign className="w-4 h-4" />
                Mis Comisiones
              </button>
              <button 
                onClick={() => setActiveTab('services')}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'services' ? 'text-white' : 'text-slate-600 hover:bg-slate-100'
                }`}
                style={activeTab === 'services' ? { backgroundColor: primaryColor } : {}}
              >
                <Package className="w-4 h-4" />
                Servicios
              </button>
              <button 
                onClick={() => setActiveTab('contract')}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'contract' ? 'text-white' : 'text-slate-600 hover:bg-slate-100'
                }`}
                style={activeTab === 'contract' ? { backgroundColor: primaryColor } : {}}
              >
                <FileText className="w-4 h-4" />
                Contrato
              </button>
              <button 
                onClick={() => setActiveTab('mydata')}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'mydata' ? 'text-white' : 'text-slate-600 hover:bg-slate-100'
                }`}
                style={activeTab === 'mydata' ? { backgroundColor: primaryColor } : {}}
              >
                <User className="w-4 h-4" />
                Mis Datos
              </button>
            </nav>
            
            {/* User Menu */}
            <div className="flex items-center gap-4">
              <Button 
                onClick={() => setShowNewModal(true)}
                className="hidden sm:flex"
                style={{ backgroundColor: primaryColor }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Lead
              </Button>
              
              <div className="relative">
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-100"
                >
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${primaryColor}20` }}
                  >
                    <span className="font-medium text-sm" style={{ color: primaryColor }}>
                      {currentUser?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  </div>
                  <span className="hidden sm:block text-sm font-medium text-slate-700">
                    {currentUser?.name?.split(' ')[0]}
                  </span>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </button>
                
                {mobileMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50">
                    <div className="px-4 py-2 border-b border-slate-100">
                      <p className="text-sm font-medium text-slate-900">{currentUser?.name}</p>
                      <p className="text-xs text-slate-500">{currentUser?.email}</p>
                      <span 
                        className="inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full"
                        style={{ backgroundColor: `${primaryColor}20`, color: primaryColor }}
                      >
                        Referidor
                      </span>
                    </div>
                    
                    {/* Mobile nav items */}
                    <div className="md:hidden py-2 border-b border-slate-100">
                      <button 
                        onClick={() => { setActiveTab('leads'); setMobileMenuOpen(false); }}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                      >
                        <Users className="w-4 h-4" /> Mis Leads
                      </button>
                      <button 
                        onClick={() => { setActiveTab('commissions'); setMobileMenuOpen(false); }}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                      >
                        <DollarSign className="w-4 h-4" /> Mis Comisiones
                      </button>
                      <button 
                        onClick={() => { setActiveTab('services'); setMobileMenuOpen(false); }}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                      >
                        <Package className="w-4 h-4" /> Servicios
                      </button>
                      <button 
                        onClick={() => { setActiveTab('contract'); setMobileMenuOpen(false); }}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                      >
                        <FileText className="w-4 h-4" /> Contrato
                      </button>
                      <button 
                        onClick={() => { setActiveTab('mydata'); setMobileMenuOpen(false); }}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                      >
                        <User className="w-4 h-4" /> Mis Datos
                      </button>
                    </div>
                    
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <LogOut className="w-4 h-4" />
                      Cerrar sesión
                    </button>
                  </div>
                )}
              </div>
              
              {/* Mobile menu button */}
              <button 
                className="md:hidden p-2 rounded-lg hover:bg-slate-100"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <Menu className="w-5 h-5 text-slate-600" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      {activeTab === 'leads' ? (
        <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          {/* Welcome Section */}
          <div 
            className="text-white rounded-2xl p-6 sm:p-8 mb-8"
            style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)` }}
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold">
                  ¡Bienvenido, {currentUser?.name?.split(' ')[0]}! 👋
                </h1>
                <p className="text-white/70 mt-2">
                  Por cada negocio cerrado de tus referidos, recibes una comisión.
                </p>
              </div>
              <Button 
                onClick={() => setShowNewModal(true)}
                className="font-semibold sm:hidden"
                style={{ backgroundColor: 'white', color: primaryColor }}
              >
                <Plus className="w-5 h-5 mr-2" />
                Nuevo Lead
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mt-6">
              <div className="bg-white/10 backdrop-blur rounded-xl p-3 sm:p-4 text-center">
                <p className="text-2xl sm:text-3xl font-bold">{stats.totalLeads || 0}</p>
                <p className="text-white/70 text-xs sm:text-sm">Leads</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-3 sm:p-4 text-center">
                <p className="text-xl sm:text-3xl font-bold truncate">{formatCurrency(stats.totalCommissionPaid || 0)}</p>
                <p className="text-white/70 text-xs sm:text-sm">Pagadas</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-3 sm:p-4 text-center">
                <p className="text-xl sm:text-3xl font-bold truncate">{formatCurrency(stats.pendingCommission || 0)}</p>
                <p className="text-white/70 text-xs sm:text-sm">Pendientes</p>
              </div>
            </div>
          </div>

          {/* Funnel View */}
          {funnelData.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-8">
              <h2 className="text-lg font-bold text-slate-900 mb-4">Estado de mis Leads</h2>
              <div className="flex flex-wrap items-center gap-1 overflow-x-auto pb-2">
                {funnelData.map((stage, index) => (
                  <FunnelStage 
                    key={stage.name}
                    stage={stage.name}
                    count={stage.count}
                    color={stage.color}
                    isLast={index === funnelData.length - 1}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Leads List */}
          <div className="bg-white rounded-2xl border border-slate-200">
            <div className="p-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900">Mis Leads Referidos</h2>
            </div>
            
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-8 h-8 border-4 border-t-transparent rounded-full mx-auto" style={{ borderColor: primaryColor, borderTopColor: 'transparent' }}></div>
              </div>
            ) : leads.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-900 mb-2">No tienes leads referidos</h3>
                <p className="text-slate-500 mb-6">Comienza a referir clientes potenciales</p>
                <Button onClick={() => setShowNewModal(true)} style={{ backgroundColor: primaryColor }}>
                  <Plus className="w-5 h-5 mr-2" /> Agregar mi primer lead
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Lead</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Estado</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Calificación</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase hidden sm:table-cell">Contacto</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Comisión</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {leads.map((lead) => (
                      <tr key={lead.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => handleViewLeadDetail(lead)}>
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-slate-900">{lead.name}</p>
                            <p className="text-xs text-slate-500">{lead.email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span 
                            className="inline-flex px-2.5 py-1 text-xs font-medium rounded-full"
                            style={{ backgroundColor: `${lead.stageColor}20`, color: lead.stageColor }}
                          >
                            {lead.stageName}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                          <StarRating 
                            rating={lead.rating || 0} 
                            size="sm"
                            readonly={true}
                          />
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <p className="text-sm text-slate-600">{lead.phone}</p>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex flex-col items-end gap-1">
                            <span className="font-semibold text-slate-900">{formatCurrency(lead.commissionAmount || 0)}</span>
                            {getStatusBadge(lead.commissionStatus)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      ) : activeTab === 'commissions' ? (
        /* Commissions Tab */
        <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Mis Comisiones</h2>
            <p className="text-slate-600 mb-6">Revisa el estado de tus comisiones por leads cerrados.</p>
            
            {/* Commission Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
              <div className="bg-slate-50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-slate-900">
                  ${(leads.reduce((sum, l) => sum + (l.commissionAmount || 0), 0)).toLocaleString('es-CO')}
                </p>
                <p className="text-xs text-slate-500 mt-1">Total Comisiones</p>
              </div>
              <div className="bg-amber-50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-amber-600">
                  ${leads.filter(l => l.commissionStatus === 'pending').reduce((sum, l) => sum + (l.commissionAmount || 0), 0).toLocaleString('es-CO')}
                </p>
                <p className="text-xs text-slate-500 mt-1">Pendientes</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">
                  ${leads.filter(l => l.commissionStatus === 'approved').reduce((sum, l) => sum + (l.commissionAmount || 0), 0).toLocaleString('es-CO')}
                </p>
                <p className="text-xs text-slate-500 mt-1">Aprobadas</p>
              </div>
              <div className="bg-emerald-50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-emerald-600">
                  ${leads.filter(l => l.commissionStatus === 'paid').reduce((sum, l) => sum + (l.commissionAmount || 0), 0).toLocaleString('es-CO')}
                </p>
                <p className="text-xs text-slate-500 mt-1">Pagadas</p>
              </div>
            </div>
            
            {/* Commissions Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Lead</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Empresa</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Monto</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.filter(l => l.commissionAmount > 0).length > 0 ? (
                    leads.filter(l => l.commissionAmount > 0).map((lead) => (
                      <tr key={lead.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4">
                          <p className="font-medium text-slate-900">{lead.name}</p>
                          <p className="text-xs text-slate-500">{lead.email}</p>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">{lead.company || '-'}</td>
                        <td className="py-3 px-4 text-right font-semibold text-slate-900">
                          ${(lead.commissionAmount || 0).toLocaleString('es-CO')}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                            lead.commissionStatus === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                            lead.commissionStatus === 'approved' ? 'bg-blue-100 text-blue-700' :
                            lead.commissionStatus === 'pending' ? 'bg-amber-100 text-amber-700' :
                            lead.commissionStatus === 'rejected' ? 'bg-red-100 text-red-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {lead.commissionStatus === 'paid' ? 'Pagada' :
                             lead.commissionStatus === 'approved' ? 'Aprobada' :
                             lead.commissionStatus === 'pending' ? 'Pendiente' :
                             lead.commissionStatus === 'rejected' ? 'Rechazada' :
                             'Sin asignar'}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="py-12 text-center">
                        <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500">No tienes comisiones registradas aún</p>
                        <p className="text-sm text-slate-400 mt-1">Las comisiones se generan cuando tus leads cierran negocios</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      ) : activeTab === 'services' ? (
        /* Services Tab - Placeholder for now, will be replaced with full Services component */
        <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <div className="bg-white rounded-2xl border border-slate-200 p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Servicios de DataLab</h2>
            <p className="text-slate-600 mb-6">Explora nuestros servicios disponibles para referir a tus clientes.</p>
            {/* Services will be loaded here - using ServicesCatalog component */}
            <ServicesCatalog />
          </div>
        </main>
      ) : activeTab === 'contract' ? (
        <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <ContractSection currentUser={currentUser} primaryColor={primaryColor} />
        </main>
      ) : activeTab === 'mydata' ? (
        /* My Data Tab - Document Upload */
        <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <MyDataSection currentUser={currentUser} primaryColor={primaryColor} />
        </main>
      ) : null}

      {/* Lead Detail Modal (Read Only) */}
      {(selectedLeadDetail || loadingDetail) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {loadingDetail ? (
              <div className="p-12 text-center">
                <div className="animate-spin w-8 h-8 border-4 border-t-transparent rounded-full mx-auto" style={{ borderColor: primaryColor, borderTopColor: 'transparent' }}></div>
                <p className="text-slate-500 mt-4">Cargando información del lead...</p>
              </div>
            ) : selectedLeadDetail ? (
              <>
                {/* Header */}
                <div className="p-6 border-b border-slate-200 rounded-t-2xl" style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)` }}>
                  <div className="flex items-start justify-between">
                    <div className="text-white">
                      <h3 className="text-xl font-bold">{selectedLeadDetail.name}</h3>
                      <p className="text-white/70 text-sm mt-1">{selectedLeadDetail.email}</p>
                    </div>
                    <button onClick={() => setSelectedLeadDetail(null)} className="p-2 hover:bg-white/20 rounded-lg">
                      <X className="w-5 h-5 text-white" />
                    </button>
                  </div>
                  <div className="mt-4">
                    <span 
                      className="inline-flex px-3 py-1.5 text-sm font-medium rounded-full bg-white/20 text-white"
                    >
                      {selectedLeadDetail.stageName || 'Sin etapa'}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                  {/* Contact Info */}
                  <div>
                    <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Información de Contacto</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                        <Phone className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="text-xs text-slate-500">Teléfono</p>
                          <p className="font-medium text-slate-900">{selectedLeadDetail.phone || '-'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                        <Mail className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="text-xs text-slate-500">Email</p>
                          <p className="font-medium text-slate-900">{selectedLeadDetail.email || '-'}</p>
                        </div>
                      </div>
                      {selectedLeadDetail.company && (
                        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                          <Building2 className="w-5 h-5 text-slate-400" />
                          <div>
                            <p className="text-xs text-slate-500">Empresa</p>
                            <p className="font-medium text-slate-900">{selectedLeadDetail.company}</p>
                          </div>
                        </div>
                      )}
                      {selectedLeadDetail.city && (
                        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                          <MapPin className="w-5 h-5 text-slate-400" />
                          <div>
                            <p className="text-xs text-slate-500">Ciudad</p>
                            <p className="font-medium text-slate-900">{selectedLeadDetail.city}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  {selectedLeadDetail.referidorDescription && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Descripción del Referido</h4>
                      <div className="p-4 rounded-lg border" style={{ backgroundColor: `${primaryColor}10`, borderColor: `${primaryColor}30` }}>
                        <p className="text-slate-700 whitespace-pre-wrap">{selectedLeadDetail.referidorDescription}</p>
                      </div>
                    </div>
                  )}

                  {/* Commission Info */}
                  <div>
                    <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Estado de Comisión</h4>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-500">Monto de comisión</p>
                          <p className="text-2xl font-bold text-slate-900">{formatCurrency(selectedLeadDetail.commissionAmount || 0)}</p>
                        </div>
                        {getStatusBadge(selectedLeadDetail.commissionStatus)}
                      </div>
                    </div>
                  </div>

                  {/* Documents */}
                  {((selectedLeadDetail.referidorDocuments && selectedLeadDetail.referidorDocuments.length > 0) || 
                    (selectedLeadDetail.documents && selectedLeadDetail.documents.length > 0)) && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Documentos Adjuntos</h4>
                      <div className="space-y-2">
                        {(selectedLeadDetail.referidorDocuments || selectedLeadDetail.documents || []).map((doc, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="p-2 rounded-lg" style={{ backgroundColor: `${primaryColor}20` }}>
                                <FileText className="w-5 h-5" style={{ color: primaryColor }} />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-slate-900">
                                  {doc.title || doc.name || `Documento ${idx + 1}`}
                                </p>
                                {doc.title && doc.name && (
                                  <p className="text-xs text-slate-500">{doc.name}</p>
                                )}
                                <p className="text-xs text-slate-400">
                                  {doc.fileType || 'Archivo'} {doc.size ? `• ${(doc.size / 1024).toFixed(1)} KB` : ''}
                                </p>
                              </div>
                            </div>
                            {doc.fileData && (
                              <a 
                                href={`data:${doc.fileType || 'application/octet-stream'};base64,${doc.fileData}`}
                                download={doc.name || `documento_${idx + 1}`}
                                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                                style={{ backgroundColor: `${primaryColor}20`, color: primaryColor }}
                              >
                                <Upload className="w-4 h-4 rotate-180" />
                                Descargar
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Timeline */}
                  <div>
                    <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Información</h4>
                    <div className="space-y-2 text-sm text-slate-600">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        <span>Creado el {new Date(selectedLeadDetail.createdAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                      </div>
                      {selectedLeadDetail.updatedAt && (
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-slate-400" />
                          <span>Última actualización: {new Date(selectedLeadDetail.updatedAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
                  <Button variant="outline" className="w-full" onClick={() => setSelectedLeadDetail(null)}>
                    Cerrar
                  </Button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      {/* New Lead Modal */}
      {showNewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-slate-900">Nuevo Lead Referido</h3>
                  <p className="text-sm text-slate-500 mt-1">Ingresa la información del cliente potencial</p>
                </div>
                <button onClick={handleCloseModal} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Nombre completo <span className="text-red-500">*</span>
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nombre del cliente"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Correo electrónico <span className="text-red-500">*</span>
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="correo@ejemplo.com"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Teléfono / Celular <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <select
                    value={formData.countryCode}
                    onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
                    className="w-24 px-2 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                  >
                    <option value="+57">🇨🇴 +57</option>
                    <option value="+1">🇺🇸 +1</option>
                    <option value="+52">🇲🇽 +52</option>
                    <option value="+34">🇪🇸 +34</option>
                    <option value="+54">🇦🇷 +54</option>
                    <option value="+56">🇨🇱 +56</option>
                    <option value="+51">🇵🇪 +51</option>
                    <option value="+593">🇪🇨 +593</option>
                    <option value="+58">🇻🇪 +58</option>
                    <option value="+507">🇵🇦 +507</option>
                    <option value="+506">🇨🇷 +506</option>
                  </select>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="300 000 0000"
                    required
                    className="flex-1"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Descripción de la conversación
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="¿Qué necesita el cliente?"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Documentos adjuntos (opcional)
                </label>
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center transition-colors" style={{ '--hover-border': primaryColor }}>
                  <input type="file" multiple className="hidden" id="documents" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" onChange={handleFileSelect} />
                  <label htmlFor="documents" className="cursor-pointer">
                    <Upload className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-600">Click para seleccionar</p>
                  </label>
                </div>
                {formData.documents.length > 0 && (
                  <div className="mt-3 space-y-3">
                    {formData.documents.map((doc, idx) => (
                      <div key={idx} className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-slate-700 flex items-center gap-2 font-medium">
                            <FileText className="w-4 h-4" style={{ color: primaryColor }} /> {doc.name}
                          </span>
                          <button type="button" onClick={() => removeDocument(idx)} className="p-1 hover:bg-slate-200 rounded">
                            <X className="w-4 h-4 text-slate-500" />
                          </button>
                        </div>
                        <input
                          type="text"
                          value={doc.title || ''}
                          onChange={(e) => updateDocumentTitle(idx, e.target.value)}
                          placeholder="Título del documento (ej: Cotización, Propuesta, Contrato...)"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={handleCloseModal} className="flex-1">Cancelar</Button>
                <Button 
                  type="submit" 
                  disabled={submitting} 
                  className="flex-1" 
                  style={{ backgroundColor: primaryColor }}
                >
                  {submitting ? 'Enviando...' : 'Enviar Lead'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// My Data Section Component - Document Upload for Referidores
function MyDataSection({ currentUser, primaryColor }) {
  const [documents, setDocuments] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({});
  const [activeSection, setActiveSection] = useState('contact'); // 'contact' or 'documents'
  
  // Contact info state
  const [contactInfo, setContactInfo] = useState({
    phones: [],
    emails: [],
    socialMedia: {}
  });
  const [newPhone, setNewPhone] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [savingContact, setSavingContact] = useState(false);

  const socialMediaTypes = [
    { key: 'instagram', label: 'Instagram', icon: Instagram, placeholder: '@usuario' },
    { key: 'facebook', label: 'Facebook', icon: Facebook, placeholder: 'facebook.com/usuario' },
    { key: 'linkedin', label: 'LinkedIn', icon: Linkedin, placeholder: 'linkedin.com/in/usuario' },
    { key: 'twitter', label: 'X (Twitter)', icon: Twitter, placeholder: '@usuario' },
    { key: 'tiktok', label: 'TikTok', icon: Globe, placeholder: '@usuario' },
    { key: 'website', label: 'Sitio Web', icon: Globe, placeholder: 'www.ejemplo.com' }
  ];

  const documentTypes = [
    { 
      key: 'cedula', 
      label: 'Cédula de Ciudadanía', 
      description: 'Sube una foto de tu cédula por ambos lados',
      icon: '🪪'
    },
    { 
      key: 'rut', 
      label: 'RUT', 
      description: 'Registro Único Tributario actualizado',
      icon: '📋'
    },
    { 
      key: 'acuerdo_referidos', 
      label: 'Acuerdo de Referidos', 
      description: 'Documento firmado del acuerdo de referidos',
      icon: '📝'
    },
    { 
      key: 'certificado_bancario', 
      label: 'Certificado Bancario', 
      description: 'Certificado de cuenta bancaria para pagos',
      icon: '🏦'
    }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [docsResponse, contactResponse] = await Promise.all([
        api.getMyReferidorDocuments(),
        api.getMyContactInfo()
      ]);
      setDocuments(docsResponse.documents || {});
      setContactInfo({
        phones: contactResponse.phones || [],
        emails: contactResponse.emails || [],
        socialMedia: contactResponse.socialMedia || {}
      });
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // Contact info handlers
  const addPhone = () => {
    if (!newPhone.trim()) return;
    if (contactInfo.phones.includes(newPhone.trim())) {
      toast.error('Este teléfono ya existe');
      return;
    }
    setContactInfo(prev => ({
      ...prev,
      phones: [...prev.phones, newPhone.trim()]
    }));
    setNewPhone('');
  };

  const removePhone = (phone) => {
    setContactInfo(prev => ({
      ...prev,
      phones: prev.phones.filter(p => p !== phone)
    }));
  };

  const addEmail = () => {
    if (!newEmail.trim()) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail.trim())) {
      toast.error('Email inválido');
      return;
    }
    if (contactInfo.emails.includes(newEmail.trim().toLowerCase())) {
      toast.error('Este email ya existe');
      return;
    }
    setContactInfo(prev => ({
      ...prev,
      emails: [...prev.emails, newEmail.trim().toLowerCase()]
    }));
    setNewEmail('');
  };

  const removeEmail = (email) => {
    setContactInfo(prev => ({
      ...prev,
      emails: prev.emails.filter(e => e !== email)
    }));
  };

  const updateSocialMedia = (key, value) => {
    setContactInfo(prev => ({
      ...prev,
      socialMedia: {
        ...prev.socialMedia,
        [key]: value
      }
    }));
  };

  const saveContactInfo = async () => {
    setSavingContact(true);
    try {
      await api.updateMyContactInfo(contactInfo);
      toast.success('Información de contacto actualizada');
    } catch (error) {
      console.error('Error saving contact info:', error);
      toast.error('Error al guardar información');
    } finally {
      setSavingContact(false);
    }
  };

  // Document handlers
  const handleFileUpload = async (docType, file) => {
    if (!file) return;

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('El archivo es demasiado grande. Máximo 10MB');
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo de archivo no permitido. Solo JPG, PNG o PDF');
      return;
    }

    setUploading(prev => ({ ...prev, [docType]: true }));

    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64Data = reader.result.split(',')[1];
        await api.uploadReferidorDocument(docType, file.name, base64Data, file.type);
        toast.success('Documento subido exitosamente');
        loadData();
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Error al subir el documento');
    } finally {
      setUploading(prev => ({ ...prev, [docType]: false }));
    }
  };

  const handleDeleteDocument = async (docType) => {
    if (!window.confirm('¿Estás seguro de eliminar este documento?')) return;

    try {
      await api.deleteReferidorDocument(docType);
      toast.success('Documento eliminado');
      setDocuments(prev => {
        const newDocs = { ...prev };
        delete newDocs[docType];
        return newDocs;
      });
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Error al eliminar el documento');
    }
  };

  const downloadDocument = (doc, docType) => {
    if (!doc?.fileData) return;
    
    const link = document.createElement('a');
    link.href = `data:${doc.fileType || 'application/octet-stream'};base64,${doc.fileData}`;
    link.download = doc.fileName || `${docType}.pdf`;
    link.click();
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-8">
        <div className="text-center py-8">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
          <p className="text-slate-500 mt-2">Cargando datos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Section Tabs */}
      <div className="bg-white rounded-2xl border border-slate-200 p-2">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveSection('contact')}
            className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
              activeSection === 'contact' 
                ? 'text-white shadow-md' 
                : 'text-slate-600 hover:bg-slate-100'
            }`}
            style={activeSection === 'contact' ? { backgroundColor: primaryColor } : {}}
          >
            <Phone className="w-4 h-4" />
            Información de Contacto
          </button>
          <button
            onClick={() => setActiveSection('documents')}
            className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
              activeSection === 'documents' 
                ? 'text-white shadow-md' 
                : 'text-slate-600 hover:bg-slate-100'
            }`}
            style={activeSection === 'documents' ? { backgroundColor: primaryColor } : {}}
          >
            <FileText className="w-4 h-4" />
            Documentos
          </button>
        </div>
      </div>

      {activeSection === 'contact' ? (
        /* Contact Info Section */
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${primaryColor}20` }}
            >
              <User className="w-6 h-6" style={{ color: primaryColor }} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Información de Contacto</h2>
              <p className="text-slate-600">Agrega tus números de teléfono, correos y redes sociales</p>
            </div>
          </div>

          {/* Phones Section */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Phone className="w-5 h-5" style={{ color: primaryColor }} />
              Teléfonos
            </h3>
            <div className="space-y-3">
              {contactInfo.phones.map((phone, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Phone className="w-4 h-4 text-slate-400" />
                  <span className="flex-1 font-medium">{phone}</span>
                  <button
                    onClick={() => removePhone(phone)}
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <div className="flex gap-2">
                <Input
                  type="tel"
                  placeholder="+57 300 123 4567"
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addPhone()}
                  className="flex-1"
                />
                <Button
                  onClick={addPhone}
                  variant="outline"
                  className="shrink-0"
                  style={{ borderColor: primaryColor, color: primaryColor }}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Agregar
                </Button>
              </div>
            </div>
          </div>

          {/* Emails Section */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5" style={{ color: primaryColor }} />
              Correos Electrónicos
            </h3>
            <div className="space-y-3">
              {contactInfo.emails.map((email, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <span className="flex-1 font-medium">{email}</span>
                  <button
                    onClick={() => removeEmail(email)}
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="correo@ejemplo.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addEmail()}
                  className="flex-1"
                />
                <Button
                  onClick={addEmail}
                  variant="outline"
                  className="shrink-0"
                  style={{ borderColor: primaryColor, color: primaryColor }}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Agregar
                </Button>
              </div>
            </div>
          </div>

          {/* Social Media Section */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5" style={{ color: primaryColor }} />
              Redes Sociales
            </h3>
            <div className="grid gap-4 sm:grid-cols-2">
              {socialMediaTypes.map((social) => {
                const Icon = social.icon;
                return (
                  <div key={social.key} className="space-y-2">
                    <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                      <Icon className="w-4 h-4" />
                      {social.label}
                    </label>
                    <Input
                      placeholder={social.placeholder}
                      value={contactInfo.socialMedia[social.key] || ''}
                      onChange={(e) => updateSocialMedia(social.key, e.target.value)}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              onClick={saveContactInfo}
              disabled={savingContact}
              className="text-white px-6"
              style={{ backgroundColor: primaryColor }}
            >
              {savingContact ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Guardar Información
            </Button>
          </div>
        </div>
      ) : (
        /* Documents Section */
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${primaryColor}20` }}
            >
              <FileText className="w-6 h-6" style={{ color: primaryColor }} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Mis Documentos</h2>
              <p className="text-slate-600">Sube tus documentos para completar tu perfil de referidor</p>
            </div>
          </div>

          {/* Info Banner */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-amber-800 font-medium">Documentos requeridos</p>
              <p className="text-amber-700 text-sm mt-1">
                Para poder recibir pagos de comisiones, debes subir todos los documentos requeridos. 
                Los formatos aceptados son JPG, PNG y PDF (máximo 10MB).
              </p>
            </div>
          </div>

          {/* Documents Grid */}
          <div className="grid gap-4">
            {documentTypes.map((docType) => {
              const doc = documents[docType.key];
              const isUploading = uploading[docType.key];

              return (
                <div 
                  key={docType.key}
                  className={`border rounded-xl p-5 transition-all ${
                    doc ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="text-3xl">{docType.icon}</div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{docType.label}</h3>
                        <p className="text-sm text-slate-500 mt-1">{docType.description}</p>
                        
                        {doc && (
                          <div className="mt-3 flex items-center gap-3">
                            <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg border border-green-200">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              <span className="text-sm text-green-700 font-medium">Subido</span>
                            </div>
                            <span className="text-xs text-slate-500">
                              {doc.fileName}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {doc ? (
                        <>
                          <button
                            onClick={() => downloadDocument(doc, docType.key)}
                            className="p-2 text-slate-600 hover:bg-white hover:text-blue-600 rounded-lg transition-colors"
                            title="Descargar"
                          >
                            <Download className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteDocument(docType.key)}
                            className="p-2 text-slate-600 hover:bg-white hover:text-red-600 rounded-lg transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </>
                      ) : (
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="image/*,.pdf"
                            className="hidden"
                            onChange={(e) => handleFileUpload(docType.key, e.target.files[0])}
                            disabled={isUploading}
                          />
                          <div 
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white transition-colors ${
                              isUploading ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'
                            }`}
                            style={{ backgroundColor: primaryColor }}
                          >
                            {isUploading ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span>Subiendo...</span>
                              </>
                            ) : (
                              <>
                                <Upload className="w-4 h-4" />
                                <span>Subir</span>
                              </>
                            )}
                          </div>
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Completion Status */}
          <div className="mt-6 p-4 bg-slate-50 rounded-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-700">Progreso de documentos</p>
                <p className="text-xs text-slate-500 mt-1">
                  {Object.keys(documents).length} de {documentTypes.length} documentos subidos
                </p>
              </div>
              <div className="flex items-center gap-2">
                {Object.keys(documents).length === documentTypes.length ? (
                  <span className="flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                    <CheckCircle className="w-4 h-4" />
                    Completo
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                    <Clock className="w-4 h-4" />
                    Pendiente
                  </span>
                )}
              </div>
            </div>
            {/* Progress bar */}
            <div className="mt-3 h-2 bg-slate-200 rounded-full overflow-hidden">
              <div 
                className="h-full rounded-full transition-all"
                style={{ 
                  width: `${(Object.keys(documents).length / documentTypes.length) * 100}%`,
                  backgroundColor: primaryColor 
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Services Catalog Component (embedded)
function ServicesCatalog() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState(null);

  useEffect(() => {
    const loadServices = async () => {
      try {
        const data = await api.getServices();
        setServices(data || []);
      } catch (error) {
        console.error('Error loading services:', error);
      } finally {
        setLoading(false);
      }
    };
    loadServices();
  }, []);

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full mx-auto"></div>
      </div>
    );
  }

  if (selectedService) {
    return <ServiceDetail service={selectedService} onBack={() => setSelectedService(null)} />;
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {services.map((service) => (
        <div 
          key={service.id}
          onClick={() => setSelectedService(service)}
          className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-lg hover:border-purple-200 transition-all cursor-pointer group"
        >
          <div className="flex items-start gap-3">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center text-white text-xl font-bold"
              style={{ backgroundColor: service.color || '#6366f1' }}
            >
              {service.icon || service.name?.charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-slate-900 group-hover:text-purple-600 transition-colors">
                {service.name}
              </h3>
              <p className="text-sm text-slate-500 line-clamp-2 mt-1">
                {service.shortDescription || service.description?.substring(0, 80)}
              </p>
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between">
            <span className="text-sm font-medium text-purple-600">
              {service.minPrice ? `Desde $${service.minPrice.toLocaleString()}` : 'Consultar precio'}
            </span>
            <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-purple-600 transition-colors" />
          </div>
        </div>
      ))}
      {services.length === 0 && (
        <div className="col-span-full text-center py-8">
          <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No hay servicios disponibles</p>
        </div>
      )}
    </div>
  );
}

// Service Detail Component
function ServiceDetail({ service, onBack }) {
  const [activeTab, setActiveTab] = useState('info');

  return (
    <div>
      {/* Back button */}
      <button onClick={onBack} className="flex items-center gap-2 text-purple-600 hover:text-purple-700 mb-4">
        <ChevronRight className="w-4 h-4 rotate-180" />
        Volver a servicios
      </button>

      {/* Service Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-2xl p-6 mb-6">
        <div className="flex items-start gap-4">
          <div 
            className="w-16 h-16 rounded-xl flex items-center justify-center text-white text-2xl font-bold bg-white/20"
          >
            {service.icon || service.name?.charAt(0)}
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold">{service.name}</h2>
            <p className="text-purple-100 mt-1">{service.category}</p>
            {service.minPrice && (
              <p className="text-lg font-semibold mt-2">
                Desde ${service.minPrice.toLocaleString()} {service.currency}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-slate-200 overflow-x-auto">
        {['info', 'video', 'presentations', 'cases'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
              activeTab === tab 
                ? 'border-purple-600 text-purple-600' 
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            {tab === 'info' && 'Información'}
            {tab === 'video' && 'Video'}
            {tab === 'presentations' && 'Presentaciones'}
            {tab === 'cases' && 'Casos de Éxito'}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'info' && (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Descripción</h3>
            <p className="text-slate-600 whitespace-pre-wrap">{service.description}</p>
          </div>
          
          {service.targetProfile && (
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Perfil Objetivo</h3>
              <p className="text-slate-600">{service.targetProfile}</p>
            </div>
          )}
          
          {service.keyFeatures?.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Características Clave</h3>
              <ul className="space-y-2">
                {service.keyFeatures.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {service.scope?.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Alcance</h3>
              <ul className="space-y-1">
                {service.scope.map((item, idx) => (
                  <li key={idx} className="text-slate-600">• {item}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {activeTab === 'video' && (
        <div>
          {service.videoUrl ? (
            <div className="aspect-video rounded-xl overflow-hidden bg-slate-900">
              <iframe
                src={service.videoUrl.replace('watch?v=', 'embed/')}
                title={service.name}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <div className="text-center py-12 bg-slate-50 rounded-xl">
              <Eye className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No hay video disponible para este servicio</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'presentations' && (
        <div>
          {service.presentations?.length > 0 ? (
            <div className="grid gap-4">
              {service.presentations.map((pres, idx) => (
                <a 
                  key={idx}
                  href={pres.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                >
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-6 h-6 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">{pres.name}</p>
                    <p className="text-sm text-slate-500">{pres.type?.toUpperCase() || 'Presentación'}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </a>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-slate-50 rounded-xl">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No hay presentaciones disponibles</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'cases' && (
        <div>
          {service.successCases?.length > 0 ? (
            <div className="grid gap-4">
              {service.successCases.map((caseItem, idx) => (
                <div key={idx} className="p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-start gap-4">
                    {caseItem.clientLogo && (
                      <img src={caseItem.clientLogo} alt={caseItem.clientName} className="w-12 h-12 rounded-lg object-cover" />
                    )}
                    <div className="flex-1">
                      <h4 className="font-semibold text-slate-900">{caseItem.title}</h4>
                      {caseItem.clientName && (
                        <p className="text-sm text-purple-600">{caseItem.clientName}</p>
                      )}
                      <p className="text-slate-600 mt-2">{caseItem.description}</p>
                      {caseItem.url && (
                        <a 
                          href={caseItem.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-purple-600 hover:underline mt-2"
                        >
                          Ver más <ChevronRight className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-slate-50 rounded-xl">
              <TrendingUp className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No hay casos de éxito disponibles</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
